import { useCallback, useEffect, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import useMyVendors from "./useMyVendors";
import {
  useAddManualMyVendorMutation,
  useHideMyVendorMutation,
  useRemoveManualMyVendorMutation,
  useUnhideMyVendorMutation,
} from "./queries";
import {
  buildMyVendorMembershipSets,
  getMyVendorMembershipState,
} from "../utils/myVendorMembershipUtils";
import {
  MY_VENDORS_BROADCAST_CHANNEL,
  broadcastMyVendorsChanged,
} from "../utils/myVendorMembershipBroadcast";

/**
 * Membership lookups + mutation handlers for My Vendors action menus
 * (My Vendors page, Search Vendors card/row).
 */
export function useMyVendorMembershipActions(sponsorId) {
  const queryClient = useQueryClient();
  const {
    providerIds,
    engagedProviderIds,
    manualProviderIds,
    hiddenProviderIds,
  } = useMyVendors(sponsorId);

  const membershipSets = useMemo(
    () =>
      buildMyVendorMembershipSets({
        providerIds,
        engagedProviderIds,
        manualProviderIds,
        hiddenProviderIds,
      }),
    [providerIds, engagedProviderIds, manualProviderIds, hiddenProviderIds]
  );

  const addManualVendorMutation = useAddManualMyVendorMutation(sponsorId);
  const removeManualVendorMutation = useRemoveManualMyVendorMutation(sponsorId);
  const hideVendorMutation = useHideMyVendorMutation(sponsorId);
  const unhideVendorMutation = useUnhideMyVendorMutation(sponsorId);

  useEffect(() => {
    if (typeof BroadcastChannel === "undefined" || !sponsorId) return undefined;
    const channel = new BroadcastChannel(MY_VENDORS_BROADCAST_CHANNEL);
    channel.onmessage = (event) => {
      if (String(event.data?.sponsorId) === String(sponsorId)) {
        queryClient.invalidateQueries({ queryKey: ["myVendors", sponsorId] });
      }
    };
    return () => channel.close();
  }, [queryClient, sponsorId]);

  const getMembership = useCallback(
    (providerId) => getMyVendorMembershipState(providerId, membershipSets),
    [membershipSets]
  );

  const resolveProviderId = (vendorOrProvider) =>
    vendorOrProvider?.id ?? vendorOrProvider?.provider_id ?? null;

  const handleAddToMyVendors = useCallback(
    async (vendorOrProvider, options = {}) => {
      const providerId = resolveProviderId(vendorOrProvider);
      if (!providerId) return;
      await addManualVendorMutation.mutateAsync(providerId);
      if (!options.skipBroadcast) {
        broadcastMyVendorsChanged(sponsorId);
      }
    },
    [addManualVendorMutation, sponsorId]
  );

  const handleRemoveFromMyVendors = useCallback(
    async (vendorOrProvider, options = {}) => {
      const providerId = resolveProviderId(vendorOrProvider);
      if (!providerId) return;
      await removeManualVendorMutation.mutateAsync(providerId);
      if (!options.skipBroadcast) {
        broadcastMyVendorsChanged(sponsorId);
      }
    },
    [removeManualVendorMutation, sponsorId]
  );

  const handleAddToHidden = useCallback(
    async (vendorOrProvider, options = {}) => {
      const providerId = resolveProviderId(vendorOrProvider);
      if (!providerId) return;
      await hideVendorMutation.mutateAsync(providerId);
      if (!options.skipBroadcast) {
        broadcastMyVendorsChanged(sponsorId);
      }
    },
    [hideVendorMutation, sponsorId]
  );

  const handleUnhide = useCallback(
    async (vendorOrProvider, options = {}) => {
      const providerId = resolveProviderId(vendorOrProvider);
      if (!providerId) return;
      await unhideVendorMutation.mutateAsync(providerId);
      if (!options.skipBroadcast) {
        broadcastMyVendorsChanged(sponsorId);
      }
    },
    [unhideVendorMutation, sponsorId]
  );

  const isMutating =
    addManualVendorMutation.isPending ||
    removeManualVendorMutation.isPending ||
    hideVendorMutation.isPending ||
    unhideVendorMutation.isPending;

  return {
    membershipSets,
    getMembership,
    handleAddToMyVendors,
    handleRemoveFromMyVendors,
    handleAddToHidden,
    handleUnhide,
    isMutating,
  };
}

export default useMyVendorMembershipActions;
